import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const [amendmentReason, setAmendmentReason] = useState<string>('');
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN'
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName
    });
    alert('Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-845">Patient not found</div>;
    const isAmending = selectedEncounter.stage === 'COMPLETED' && amendmentReason.trim().length > 0;
    return <div className="space-y-6 pb-20" data-id="element-846">
        {isAmending && <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 print:hidden" data-id="element-847">
            <div className="flex items-start gap-3" data-id="element-848">
              <History className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" data-id="element-849" />
              <div className="flex-1" data-id="element-850">
                <p className="font-bold text-amber-900 text-sm uppercase tracking-wide" data-id="element-851">
                  Amending Completed Visit
                </p>
                <p className="text-sm text-amber-800 mt-1" data-id="element-852">
                  <span className="font-semibold" data-id="element-853">Reason:</span>{' '}
                  {amendmentReason}
                </p>
                <p className="text-xs text-amber-700 mt-1 italic" data-id="element-854">
                  Every save will be recorded in the audit trail with your name,
                  the timestamp, and this reason.
                </p>
              </div>
            </div>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-855">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-856">
            {isAmending ? 'Amend Consultation' : 'Consultation'}
          </h1>
          <div className="flex space-x-2" data-id="element-857">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-858">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-859">
              ↩ Send to Nursing
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-860">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-861">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-862" /> Complete Visit
              </Button>}
          </div>
        </div>

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-863">
          {/* ═══ FORM HEADER ═══ */}
          <div className="flex justify-between items-start mb-6" data-id="element-864">
            <div className="flex items-center" data-id="element-865">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-866" />
              <div data-id="element-867">
                <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-868">
                  BMED Medical Centre
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-869">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-870">
              <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-871">
                Confidential
              </p>
              <p className="text-sm" data-id="element-872">
                <span className="font-semibold" data-id="element-873">Date:</span>{' '}
                {new Date(selectedEncounter.startedAt).toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-874">
                <span className="font-semibold" data-id="element-875">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* ═══ PERSONAL DETAILS ═══ */}
          <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-876">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-877">
              <div className="col-span-1 font-semibold" data-id="element-878">1.</div>
              <div className="col-span-6" data-id="element-879">
                <span className="font-semibold" data-id="element-880">Full Names:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-3" data-id="element-881">
                <span className="font-semibold" data-id="element-882">Sex:</span> {patient.sex}
              </div>
              <div className="col-span-2" data-id="element-883">
                <span className="font-semibold" data-id="element-884">Age:</span> {patient.age}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-885">
              <div className="col-span-1 font-semibold" data-id="element-886">2.</div>
              <div className="col-span-11" data-id="element-887">
                <span className="font-semibold" data-id="element-888">Marital Status:</span>{' '}
                {patient.maritalStatus}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-889">
              <div className="col-span-1 font-semibold" data-id="element-890">3.</div>
              <div className="col-span-7" data-id="element-891">
                <span className="font-semibold" data-id="element-892">Home Address:</span>{' '}
                {patient.address}
              </div>
              <div className="col-span-4" data-id="element-893">
                <span className="font-semibold" data-id="element-894">Contact:</span>{' '}
                {patient.contact}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-895">
              <div className="col-span-1 font-semibold" data-id="element-896">4.</div>
              <div className="col-span-11" data-id="element-897">
                <span className="font-semibold" data-id="element-898">Occupation:</span>{' '}
                {patient.occupation || '_________________________'}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-899">
              <div className="col-span-1 font-semibold" data-id="element-900">5.</div>
              <div className="col-span-5" data-id="element-901">
                <span className="font-semibold" data-id="element-902">Next of Kin:</span>{' '}
                {patient.nextOfKin}
              </div>
              <div className="col-span-3" data-id="element-903">
                <span className="font-semibold" data-id="element-904">Contact:</span>{' '}
                {patient.nokContact}
              </div>
              <div className="col-span-3" data-id="element-905">
                <span className="font-semibold" data-id="element-906">Relationship:</span>{' '}
                {patient.nokRelationship}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-907">
              <div className="col-span-1 font-semibold" data-id="element-908">6.</div>
              <div className="col-span-11" data-id="element-909">
                <span className="font-semibold" data-id="element-910">Allergies:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-911">
                  {patient.allergies}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-912">
              <div className="col-span-1 font-semibold" data-id="element-913">7.</div>
              <div className="col-span-11" data-id="element-914">
                <span className="font-semibold" data-id="element-915">HIV Status:</span>{' '}
                <span className="ml-4" data-id="element-916">
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-917">
                    Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-918">
                    Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                  </span>
                  <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-919">
                    Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* ═══ NURSING OBSERVATIONS ═══ */}
          <div className="mb-6" data-id="element-920">
            <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-921">
              Nursing Observations
            </h3>
            <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-922">
              <thead data-id="element-923">
                <tr className="bg-gray-50" data-id="element-924">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-925">
                      {n}
                    </th>)}
                </tr>
                <tr className="bg-gray-100" data-id="element-926">
                  <th className="border border-black p-1" data-id="element-927">Temp</th>
                  <th className="border border-black p-1" data-id="element-928">Pulse</th>
                  <th className="border border-black p-1" data-id="element-929">Resp</th>
                  <th className="border border-black p-1" data-id="element-930">BP</th>
                  <th className="border border-black p-1" data-id="element-931">SpO2</th>
                  <th className="border border-black p-1" data-id="element-932">Weight (Kg)</th>
                  <th className="border border-black p-1" data-id="element-933">Height</th>
                  <th className="border border-black p-1" data-id="element-934">BMI</th>
                  <th className="border border-black p-1" data-id="element-935">Pain Score</th>
                  <th className="border border-black p-1" data-id="element-936">AVPU</th>
                </tr>
              </thead>
              <tbody data-id="element-937">
                <tr data-id="element-938">
                  <td className="border border-black p-2 font-medium" data-id="element-939">
                    {selectedEncounter.vitals?.temp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-940">
                    {selectedEncounter.vitals?.pulse || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-941">
                    {selectedEncounter.vitals?.resp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-942">
                    {selectedEncounter.vitals?.bp || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-943">
                    {selectedEncounter.vitals?.sp02 || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-944">
                    {selectedEncounter.vitals?.weight || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-945">
                    {selectedEncounter.vitals?.height || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-946">
                    {selectedEncounter.vitals?.bmi || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-947">
                    {selectedEncounter.vitals?.painScore || ''}
                  </td>
                  <td className="border border-black p-2 font-medium" data-id="element-948">
                    {selectedEncounter.vitals?.avpu || ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
          {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-949">
                <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-950">
                  ⚡ Stat Doses Administered by Nursing
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-951">
                  <thead data-id="element-952">
                    <tr className="bg-gray-100" data-id="element-953">
                      <th className="border border-black p-1.5 w-10" data-id="element-954">No.</th>
                      <th className="border border-black p-1.5 text-left" data-id="element-955">
                        Drug Name
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-956">
                        Dose
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-957">
                        Route
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-958">
                        Time Given
                      </th>
                      <th className="border border-black p-1.5 text-left" data-id="element-959">
                        Nurse
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-960">
                    {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-961">
                        <td className="border border-black p-1.5 text-center" data-id="element-962">
                          {idx + 1}
                        </td>
                        <td className="border border-black p-1.5 font-medium" data-id="element-963">
                          {dose.drugName}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-964">
                          {dose.dose}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-965">
                          {dose.route}
                        </td>
                        <td className="border border-black p-1.5" data-id="element-966">
                          {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                        </td>
                        <td className="border border-black p-1.5 italic text-xs" data-id="element-967">
                          {dose.nurseName || ''}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

          {/* ═══ CLINICAL NOTES ═══ */}
          <div className="space-y-5 mb-6 text-sm" data-id="element-968">
            {/* PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-969">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-970">
                Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
              ...notes,
              presentingComplaint: e.target.value
            })} data-id="element-971" />
            </div>

            {/* HISTORY OF PRESENTING COMPLAINT */}
            <div className="border-2 border-black" data-id="element-972">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-973">
                History of Presenting Complaint
              </h3>
              <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
              ...notes,
              historyOfPresentingComplaint: e.target.value
            })} data-id="element-974" />
            </div>

            {/* PAST MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-975">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-976">
                Past Medical History / General Co-Morbidities
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-977">
                <tbody data-id="element-978">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                  const checked = notes.pastMedicalHistory?.includes(condition) || false;
                  return <tr key={condition} className="border-b border-gray-300" data-id="element-979">
                        <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-980">
                          {condition}
                        </td>
                        <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-981">
                          <label className="flex items-center justify-center cursor-pointer" data-id="element-982">
                            <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                          const current = notes.pastMedicalHistory || [];
                          if (e.target.checked) {
                            setNotes({
                              ...notes,
                              pastMedicalHistory: [...current, condition]
                            });
                          } else {
                            setNotes({
                              ...notes,
                              pastMedicalHistory: current.filter(c => c !== condition)
                            });
                          }
                        }} data-id="element-983" />
                            {checked ? 'Yes' : ''}
                          </label>
                        </td>
                        <td className="p-2" data-id="element-984">
                          <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                        ...notes,
                        pastMedicalHistoryNotes: {
                          ...(notes.pastMedicalHistoryNotes || {}),
                          [condition]: e.target.value
                        }
                      })} data-id="element-985" />
                        </td>
                      </tr>;
                })}
                </tbody>
              </table>
            </div>

            {/* DRUG HISTORY */}
            <div className="border-2 border-black" data-id="element-986">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-987">
                Drug History
              </h3>
              <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
              ...notes,
              drugHistory: e.target.value
            })} data-id="element-988" />
              <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-989">
                <span className="font-semibold" data-id="element-990">Any known allergy:</span>{' '}
                <span className="text-red-600 font-medium" data-id="element-991">
                  {patient.allergies}
                </span>
              </div>
            </div>

            {/* FAMILY MEDICAL HISTORY */}
            <div className="border-2 border-black" data-id="element-992">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-993">
                Family Medical History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-994">
                <tbody data-id="element-995">
                  {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                  const checked = notes.familyMedicalHistory?.includes(condition) || false;
                  return <tr key={condition} className="border-b border-gray-300" data-id="element-996">
                        <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-997">
                          {condition}
                        </td>
                        <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-998">
                          <label className="flex items-center justify-center cursor-pointer" data-id="element-999">
                            <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                          const current = notes.familyMedicalHistory || [];
                          if (e.target.checked) {
                            setNotes({
                              ...notes,
                              familyMedicalHistory: [...current, condition]
                            });
                          } else {
                            setNotes({
                              ...notes,
                              familyMedicalHistory: current.filter(c => c !== condition)
                            });
                          }
                        }} data-id="element-1000" />
                            {checked ? 'Yes' : ''}
                          </label>
                        </td>
                        <td className="p-2" data-id="element-1001">
                          <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                        ...notes,
                        familyMedicalHistoryNotes: {
                          ...(notes.familyMedicalHistoryNotes || {}),
                          [condition]: e.target.value
                        }
                      })} data-id="element-1002" />
                        </td>
                      </tr>;
                })}
                  {(() => {
                  const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                  return <tr className="border-b border-gray-300" data-id="element-1003">
                        <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-1004">
                          OTHER
                        </td>
                        <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-1005">
                          <label className="flex items-center justify-center cursor-pointer" data-id="element-1006">
                            <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                          const current = notes.familyMedicalHistory || [];
                          if (e.target.checked) {
                            setNotes({
                              ...notes,
                              familyMedicalHistory: [...current, 'OTHER']
                            });
                          } else {
                            setNotes({
                              ...notes,
                              familyMedicalHistory: current.filter(c => c !== 'OTHER')
                            });
                          }
                        }} data-id="element-1007" />
                            {otherChecked ? 'Yes' : ''}
                          </label>
                        </td>
                        <td className="p-2" data-id="element-1008">
                          <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                        ...notes,
                        familyMedicalHistoryOther: e.target.value
                      })} data-id="element-1009" />
                        </td>
                      </tr>;
                })()}
                </tbody>
              </table>
            </div>

            {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
            <div className="border-2 border-black" data-id="element-1010">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-1011">
                Obstetrics and Gynaecology History
              </h3>
              <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
              ...notes,
              obgynHistory: e.target.value
            })} data-id="element-1012" />
            </div>

            {/* SOCIAL HISTORY */}
            <div className="border-2 border-black" data-id="element-1013">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1014">
                Social History
              </h3>
              <table className="w-full border-collapse text-sm" data-id="element-1015">
                <tbody data-id="element-1016">
                  <tr className="border-b border-gray-300" data-id="element-1017">
                    <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-1018">
                      Alcohol
                    </td>
                    <td className="p-2" data-id="element-1019">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        alcohol: e.target.value
                      }
                    })} data-id="element-1020" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-1021">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-1022">
                      Smoking
                    </td>
                    <td className="p-2" data-id="element-1023">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        smoking: e.target.value
                      }
                    })} data-id="element-1024" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-300" data-id="element-1025">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-1026">
                      Occupation
                    </td>
                    <td className="p-2" data-id="element-1027">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        occupation: e.target.value
                      }
                    })} data-id="element-1028" />
                    </td>
                  </tr>
                  <tr data-id="element-1029">
                    <td className="border-r border-gray-300 p-2 font-medium" data-id="element-1030">
                      Mental Status
                    </td>
                    <td className="p-2" data-id="element-1031">
                      <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                      ...notes,
                      socialHistory: {
                        ...notes.socialHistory,
                        mentalStatus: e.target.value
                      }
                    })} data-id="element-1032" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* REVIEW OF SYSTEMS */}
            <div className="border-2 border-black" data-id="element-1033">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1034">
                Review of Systems
              </h3>
              <div className="p-3 space-y-2" data-id="element-1035">
                {[{
                key: 'rs',
                label: 'R/S:'
              }, {
                key: 'cvs',
                label: 'CVS:'
              }, {
                key: 'cns',
                label: 'CNS:'
              }, {
                key: 'git',
                label: 'GIT:'
              }, {
                key: 'gut',
                label: 'GUT:'
              }, {
                key: 'mss',
                label: 'MSS:'
              }, {
                key: 'is',
                label: 'IS:'
              }].map(system => <div key={system.key} className="flex items-center" data-id="element-1036">
                    <span className="font-semibold w-16" data-id="element-1037">{system.label}</span>
                    <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                  ...notes,
                  reviewOfSystems: {
                    ...notes.reviewOfSystems,
                    [system.key]: e.target.value
                  }
                })} data-id="element-1038" />
                  </div>)}
              </div>
            </div>

            {/* ON EXAMINATION */}
            <div className="border-2 border-black" data-id="element-1039">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-1040">
                On Examination
              </h3>
              <div className="p-4 space-y-3" data-id="element-1041">
                {[{
                key: 'gc' as const,
                label: 'G/C',
                helper: 'Stable / Ill Looking / Unconscious'
              }, {
                key: 'febrile' as const,
                label: 'Temperature',
                helper: 'Febrile / Afebrile / Lethargic'
              }, {
                key: 'pjc' as const,
                label: 'P | J | C',
                helper: 'Pallor / Jaundice / Cyanosis'
              }, {
                key: 's1s2' as const,
                label: 'S1  S2',
                helper: 'Heart sounds'
              }, {
                key: 'rs' as const,
                label: 'R/S',
                helper: 'Respiratory system'
              }, {
                key: 'pa' as const,
                label: 'P/A',
                helper: 'Per abdomen'
              }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-1042">
                    <div className="pt-2" data-id="element-1043">
                      <p className="font-bold text-sm text-gray-900" data-id="element-1044">
                        {field.label}
                      </p>
                      <p className="text-xs text-gray-500 leading-tight" data-id="element-1045">
                        {field.helper}
                      </p>
                    </div>
                    <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                  ...notes,
                  onExaminationDetails: {
                    ...(notes.onExaminationDetails || {
                      gc: '',
                      febrile: '',
                      pjc: '',
                      s1s2: '',
                      rs: '',
                      pa: ''
                    }),
                    [field.key]: e.target.value
                  }
                })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-1046" />
                  </div>)}

                {/* Additional notes (free text) */}
                <div className="pt-3 border-t border-gray-200" data-id="element-1047">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-1048">
                    Additional Notes
                  </label>
                  <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                  ...notes,
                  onExamination: e.target.value
                })} data-id="element-1049" />
                </div>
              </div>
            </div>

            {/* SUMMARY OF SIGNIFICANT FINDINGS */}
            <div className="border-2 border-black" data-id="element-1050">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-1051">
                Summary of Significant Findings
              </h3>
              <div className="p-3 space-y-2" data-id="element-1052">
                {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-1053">
                    <span className="font-medium mr-2 mt-1" data-id="element-1054">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                  const current = notes.significantFindings || Array(8).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    significantFindings: updated
                  });
                }} data-id="element-1055" />
                  </div>)}
              </div>
            </div>

            {/* WORKING DIAGNOSIS */}
            <div className="border-2 border-black" data-id="element-1056">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-1057">
                Working Diagnosis
              </h3>
              <div className="p-3 space-y-2" data-id="element-1058">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-1059">
                    <span className="font-medium mr-2 mt-1" data-id="element-1060">{i + 1}.</span>
                    <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                  const current = notes.workingDiagnosis || Array(10).fill('');
                  const updated = [...current];
                  updated[i] = e.target.value;
                  setNotes({
                    ...notes,
                    workingDiagnosis: updated
                  });
                }} data-id="element-1061" />
                  </div>)}
              </div>
            </div>

            {/* INVESTIGATIONS TO BE DONE */}
            <div className="border-2 border-black" data-id="element-1062">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1063">
                Investigations To Be Done
              </h3>
              <div className="p-4" data-id="element-1064">
                {/* LABORATORY TESTS */}
                <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-1065">
                  Laboratory Tests
                </h4>
                <table className="w-full border-collapse text-sm mb-4" data-id="element-1066">
                  <tbody data-id="element-1067">
                    {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-1068">
                        {pair.map((test, ci) => {
                      if (!test) {
                        return <Fragment key={`empty-${ci}`} data-id="element-1069">
                                <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-1070"></td>
                                <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-1071"></td>
                              </Fragment>;
                      }
                      const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                      return <Fragment key={test} data-id="element-1072">
                              <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-1073">
                                {test}
                              </td>
                              <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-1074">
                                {isOrdered ? <span className="text-green-700 font-bold" data-id="element-1075">
                                    ✓
                                  </span> : ''}
                              </td>
                            </Fragment>;
                    })}
                      </tr>)}
                  </tbody>
                </table>

                {/* RADIOLOGY SCANS */}
                <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-1076">
                  Radiology Scans
                </h4>
                <table className="w-full border-collapse text-sm mb-4" data-id="element-1077">
                  <tbody data-id="element-1078">
                    {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-1079">
                        {pair.map((scan, ci) => {
                      if (!scan) {
                        return <Fragment key={`empty-r-${ci}`} data-id="element-1080">
                                <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-1081"></td>
                                <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-1082"></td>
                              </Fragment>;
                      }
                      const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                        const a = r.scanType.toLowerCase();
                        const b = scan.toLowerCase();
                        return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                      });
                      return <Fragment key={scan} data-id="element-1083">
                              <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-1084">
                                {scan}
                              </td>
                              <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-1085">
                                {isOrdered ? <span className="text-green-700 font-bold" data-id="element-1086">
                                    ✓
                                  </span> : ''}
                              </td>
                            </Fragment>;
                    })}
                      </tr>)}
                  </tbody>
                </table>

                {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                {(() => {
                const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                if (!hasAny) return null;
                return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-1087">
                      <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-1088">
                        Pending Investigations (Recall to make changes)
                      </h4>
                      <ul className="space-y-1.5 text-sm" data-id="element-1089">
                        {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-1090">
                              <span data-id="element-1091">
                                <span className="font-medium" data-id="element-1092">{testName}</span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-1093">
                                  · Laboratory ·{' '}
                                  {new Date(lab.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-1094">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-1095" /> Recall
                              </Button>
                            </li>))}
                        {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-1096">
                              <span data-id="element-1097">
                                <span className="font-medium" data-id="element-1098">
                                  {lab.otherTests}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-1099">
                                  · Other lab test
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-1100">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-1101" /> Recall
                              </Button>
                            </li>)}
                        {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-1102">
                            <span data-id="element-1103">
                              <span className="font-medium" data-id="element-1104">
                                {rad.scanType}
                              </span>{' '}
                              <span className="text-xs text-gray-500" data-id="element-1105">
                                · Radiology ·{' '}
                                {new Date(rad.requestedAt).toLocaleTimeString()}
                              </span>
                            </span>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-1106">
                              <RotateCcw className="w-3 h-3 mr-1" data-id="element-1107" /> Recall
                            </Button>
                          </li>)}
                      </ul>
                      <p className="text-xs text-amber-700 mt-2 italic" data-id="element-1108">
                        Recall removes the request before the lab or radiology
                        department processes it. Once a test is completed, it
                        becomes part of the permanent record.
                      </p>
                    </div>;
              })()}

                <div className="flex gap-3 mb-4" data-id="element-1109">
                  <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-1110">
                    <FlaskConical className="w-4 h-4 mr-2" data-id="element-1111" /> Request Laboratory
                    Test
                  </Button>
                  <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-1112">
                    <Scan className="w-4 h-4 mr-2" data-id="element-1113" /> Request Radiology Scan
                  </Button>
                </div>

                <div className="border-t border-gray-300 pt-2" data-id="element-1114">
                  <span className="font-semibold" data-id="element-1115">Other investigations:</span>{' '}
                  {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                </div>

                {/* Investigation Results — View File per test */}
                {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-1116">
                    <h4 className="font-bold mb-3 uppercase text-center" data-id="element-1117">
                      Investigation Results
                    </h4>
                    <table className="w-full border-collapse text-sm" data-id="element-1118">
                      <thead data-id="element-1119">
                        <tr className="bg-gray-100" data-id="element-1120">
                          <th className="border border-black p-1.5 text-left" data-id="element-1121">
                            Test / Scan
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-1122">
                            Type
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-1123">
                            Performed By
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-1124">
                            Completed
                          </th>
                          <th className="border border-black p-1.5 text-center w-32" data-id="element-1125">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-1126">
                        {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-1127">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-1128">
                                    {testName}
                                  </td>
                                  <td className="border border-black p-1.5" data-id="element-1129">
                                    Laboratory
                                  </td>
                                  <td className="border border-black p-1.5" data-id="element-1130">
                                    —
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-1131">
                                    {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                  </td>
                                  <td className="border border-black p-1.5 text-center" data-id="element-1132">
                                    <div className="flex justify-center gap-2" data-id="element-1133">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            labId: lab.id,
                            testName
                          })} data-id="element-1134">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-1135" />{' '}
                                        View File
                                      </Button>
                                      <button onClick={() => setRemoveTestModal({
                            isOpen: true,
                            labId: lab.id,
                            testName,
                            reason: ''
                          })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-1136">
                                        <Trash2 className="w-4 h-4" data-id="element-1137" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>))}
                        {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-1138">
                              <td className="border border-black p-1.5 font-medium" data-id="element-1139">
                                {lab.otherTests}{' '}
                                <span className="text-xs text-gray-500" data-id="element-1140">
                                  (Other)
                                </span>
                              </td>
                              <td className="border border-black p-1.5" data-id="element-1141">
                                Laboratory
                              </td>
                              <td className="border border-black p-1.5" data-id="element-1142">—</td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-1143">
                                {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 text-center" data-id="element-1144">
                                <div className="flex justify-center gap-2" data-id="element-1145">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            labId: lab.id,
                            testName: lab.otherTests
                          })} data-id="element-1146">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-1147" /> View
                                    File
                                  </Button>
                                  <button onClick={() => setRemoveTestModal({
                            isOpen: true,
                            labId: lab.id,
                            testName: lab.otherTests,
                            reason: ''
                          })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-1148">
                                    <Trash2 className="w-4 h-4" data-id="element-1149" />
                                  </button>
                                </div>
                              </td>
                            </tr>)}
                        {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-1150">
                              <td className="border border-black p-1.5 font-medium" data-id="element-1151">
                                {rad.scanType}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-1152">
                                Radiology
                              </td>
                              <td className="border border-black p-1.5" data-id="element-1153">
                                {rad.radiographerName || '—'}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-1154">
                                {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 text-center" data-id="element-1155">
                                <Button size="sm" variant="outline" onClick={() => setViewingTest({
                          kind: 'rad',
                          radId: rad.id
                        })} data-id="element-1156">
                                  <Eye className="w-3.5 h-3.5 mr-1" data-id="element-1157" /> View File
                                </Button>
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>

            {/* DRUG TREATMENT / PRESCRIPTION */}
            <div className="border-2 border-black" data-id="element-1158">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1159">
                Drug Treatment / Prescription
                {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-1160">
                    ({selectedEncounter.prescriptions.length} of 20 drugs)
                  </span>}
              </h3>
              <div className="max-h-[420px] overflow-y-auto" data-id="element-1161">
                <table className="w-full border-collapse text-sm text-center" data-id="element-1162">
                  <thead data-id="element-1163">
                    <tr className="bg-gray-100" data-id="element-1164">
                      <th className="border border-black p-1.5 w-10" data-id="element-1165">No.</th>
                      <th className="border border-black p-1.5 text-left" data-id="element-1166">
                        Drug Name
                      </th>
                      <th className="border border-black p-1.5" data-id="element-1167">Dose</th>
                      <th className="border border-black p-1.5" data-id="element-1168">Route</th>
                      <th className="border border-black p-1.5" data-id="element-1169">Frequency</th>
                      <th className="border border-black p-1.5" data-id="element-1170">No. of Days</th>
                      <th className="border border-black p-1.5" data-id="element-1171">Signature</th>
                      <th className="border border-black p-1.5 w-16" data-id="element-1172">Action</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1173">
                    {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Not Dispensed' ? 'bg-red-50' : ''} data-id="element-1174">
                            <td className="border border-black p-1.5" data-id="element-1175">
                              {i + 1}
                            </td>
                            <td className="border border-black p-1.5 text-left font-medium" data-id="element-1176">
                              {rx.drugName}
                            </td>
                            <td className="border border-black p-1.5" data-id="element-1177">
                              {rx.dose}
                            </td>
                            <td className="border border-black p-1.5" data-id="element-1178">
                              {rx.route}
                            </td>
                            <td className="border border-black p-1.5" data-id="element-1179">
                              {rx.frequency}
                            </td>
                            <td className="border border-black p-1.5" data-id="element-1180">
                              {rx.days}
                            </td>
                            <td className="border border-black p-1.5 text-xs" data-id="element-1181">
                              {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-1182">
                                  ✓ Dispensed
                                </span>}
                              {rx.status === 'Not Dispensed' && <span className="text-red-700 font-bold" data-id="element-1183">
                                  ✗ Not Available
                                </span>}
                              {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-1184">
                                  Pending
                                </span>}
                              {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-1185">
                                  {rx.pharmacistName}
                                </div>}
                            </td>
                            <td className="border border-black p-1.5" data-id="element-1186">
                              {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-1187">
                                  <Trash2 className="w-4 h-4" data-id="element-1188" />
                                </button> : <span className="text-gray-300" data-id="element-1189">—</span>}
                            </td>
                          </tr>) : Array.from({
                    length: 20
                  }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-1190">
                            <td className="border border-black p-3" data-id="element-1191">{i}</td>
                            <td className="border border-black p-3" data-id="element-1192"></td>
                            <td className="border border-black p-3" data-id="element-1193"></td>
                            <td className="border border-black p-3" data-id="element-1194"></td>
                            <td className="border border-black p-3" data-id="element-1195"></td>
                            <td className="border border-black p-3" data-id="element-1196"></td>
                            <td className="border border-black p-3" data-id="element-1197"></td>
                            <td className="border border-black p-3" data-id="element-1198"></td>
                          </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-1199">
                <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-1200">
                  <Pill className="w-4 h-4 mr-2" data-id="element-1201" />
                  {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                </Button>
                {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-1202">
                    <Pill className="w-4 h-4 mr-2" data-id="element-1203" /> Send Prescription to
                    Pharmacy
                  </Button>}
              </div>
            </div>

            {/* FOLLOW UP / REVIEW DATE */}
            <div className="border-2 border-black" data-id="element-1204">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-1205">
                Next Follow Up / Review Date (If Any)
              </h3>
              <table className="w-full border-collapse text-sm text-center" data-id="element-1206">
                <thead data-id="element-1207">
                  <tr className="bg-gray-100" data-id="element-1208">
                    <th className="border border-black p-1 w-10" data-id="element-1209">No.</th>
                    <th className="border border-black p-1" data-id="element-1210">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-1211">No.</th>
                    <th className="border border-black p-1" data-id="element-1212">Date</th>
                    <th className="border border-black p-1 w-10" data-id="element-1213">No.</th>
                    <th className="border border-black p-1" data-id="element-1214">Date</th>
                  </tr>
                </thead>
                <tbody data-id="element-1215">
                  {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-1216">
                      <td className="border border-black p-1 font-medium" data-id="element-1217">
                        {row + 1}
                      </td>
                      <td className="border border-black p-1" data-id="element-1218">
                        {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                      if (e.target.value) {
                        updateEncounter(selectedEncounter.id, {
                          reviewDate: new Date(e.target.value).toISOString()
                        });
                      }
                    }} data-id="element-1219" /> : ''}
                      </td>
                      <td className="border border-black p-1 font-medium" data-id="element-1220">
                        {row + 6}
                      </td>
                      <td className="border border-black p-2" data-id="element-1221"></td>
                      <td className="border border-black p-1 font-medium" data-id="element-1222">
                        {row + 11}
                      </td>
                      <td className="border border-black p-2" data-id="element-1223"></td>
                    </tr>)}
                </tbody>
              </table>
              <div className="border-t border-black p-3" data-id="element-1224">
                <h4 className="font-bold uppercase text-sm mb-2" data-id="element-1225">Notes</h4>
                <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={selectedEncounter.reviewDateNotes || ''} onChange={e => updateEncounter(selectedEncounter.id, {
                reviewDateNotes: e.target.value
              })} data-id="element-1226" />
              </div>
            </div>

            {/* NAME OF MEDICAL PRACTITIONER */}
            <div className="border-2 border-black p-4 mt-6" data-id="element-1227">
              <p className="font-bold text-sm" data-id="element-1228">
                Name of the Medical Practitioner:{' '}
                <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-1229">
                  {currentUser?.fullName || ''}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1230">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-1231">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-1232">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-1233" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-1234">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-1235">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-1236" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-1237">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-1238">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-1239">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1240">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-1241">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1242">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1243">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-1244">
                  <X className="w-5 h-5 text-gray-400" data-id="element-1245" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-1246">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-1247">
                  <div data-id="element-1248">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1249">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1250">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1251" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-1252">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1253">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1254">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1255" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-1256">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1257">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1258">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1259" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-1260">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1261">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1262">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1263" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-1264">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1265">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1266">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1267" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-1268">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1269">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1270">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1271" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-1272">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-1273">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-1274">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-1275" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-1276">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-1277" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-1278">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-1279">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-1280">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1281">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-1282">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-1283">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-1284">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-1285">
                  <X className="w-5 h-5 text-gray-400" data-id="element-1286" />
                </button>
              </div>
              <div className="p-6" data-id="element-1287">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1288">
                  Scan Type <span className="text-red-500" data-id="element-1289">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-1290">
                  <option value="" data-id="element-1291">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-1292">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-1293">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-1294">OBS Scan</option>
                  <option value="CXR (Chest X-Ray)" data-id="element-1295">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-1296">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-1297">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-1298">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-1299">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-1300">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-1301">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-1302">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-1303">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1304">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-1305">
                    <X className="w-5 h-5 text-gray-400" data-id="element-1306" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-1307">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1308">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-1309" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-1310" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-1311" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-1312" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-1313" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-1314">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-1315">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-1316">
                      <div className="flex justify-between items-center mb-2" data-id="element-1317">
                        <h4 className="font-medium text-sm" data-id="element-1318">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-1319">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-1320">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-1321">
                            <span className="flex-1 min-w-0 truncate" data-id="element-1322">
                              <span className="text-gray-400 mr-2" data-id="element-1323">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-1324">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-1325">
                                <Trash2 className="w-4 h-4" data-id="element-1326" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-1327">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-1328">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-1329">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-1330">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-1331">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-1332">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-1333">
                <X className="w-6 h-6" data-id="element-1334" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-1335">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-1336" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-1337">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-1338">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-1339">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-1340">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-1341">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-1342">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-1343">
                        <Printer className="w-4 h-4 mr-1" data-id="element-1344" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-1345">
                        <Download className="w-4 h-4 mr-1" data-id="element-1346" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-1347">
                        <X className="w-5 h-5 text-gray-500" data-id="element-1348" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-1349">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-1350">
                      <div className="flex items-center" data-id="element-1351">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-1352" />
                        <div data-id="element-1353">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-1354">
                            BMED Medical Centre
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-1355">
                            Maternity Care Made Better
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-1356">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-1357">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-1358">
                          <span className="font-semibold" data-id="element-1359">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-1360">
                          <span className="font-semibold" data-id="element-1361">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-1362">
                          <span className="font-semibold" data-id="element-1363">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-1364">
                          <span className="font-semibold" data-id="element-1365">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-1366">
                            <span className="font-semibold" data-id="element-1367">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-1368">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-1369">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-1370">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1371">
                              <h3 className="font-bold uppercase" data-id="element-1372">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-1373">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-1374">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-1375">
                                  <thead data-id="element-1376">
                                    <tr className="bg-gray-50" data-id="element-1377">
                                      <th className="border border-black p-1.5 text-left" data-id="element-1378">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-1379">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-1380">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-1381">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-1382">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-1383">
                                        <td className="border border-black p-1.5" data-id="element-1384">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-1385">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1386">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1387">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-1388">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-1389">
                              <h3 className="font-bold uppercase" data-id="element-1390">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-1391">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-1392">
                                    <span className="w-16 font-bold" data-id="element-1393">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-1394">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-1395">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-1396">
                              <div data-id="element-1397">
                                <p className="font-semibold mb-2" data-id="element-1398">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-1399">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-1400">
                                <p className="font-semibold mb-2" data-id="element-1401">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-1402">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-1403">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1404">
                              <h3 className="font-bold uppercase" data-id="element-1405">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-1406">
                              <thead data-id="element-1407">
                                <tr className="bg-gray-100" data-id="element-1408">
                                  <th className="border border-black p-1.5 text-left" data-id="element-1409">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-1410">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1411">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1412">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-1413">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1414">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-1415">
                                    <td className="border border-black p-1.5" data-id="element-1416">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-1417">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1418">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1419">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-1420">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-1421">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1422">
                              <h3 className="font-bold uppercase" data-id="element-1423">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-1424">
                              <thead data-id="element-1425">
                                <tr className="bg-gray-100" data-id="element-1426">
                                  <th className="border border-black p-1.5 text-left" data-id="element-1427">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-1428">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1429">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-1430">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1431">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-1432">
                                    <tr className="align-top" data-id="element-1433">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-1434">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-1435">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-1436">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-1437">
                                            <span className="font-medium" data-id="element-1438">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-1439">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-1440">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-1441">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-1442">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1443">
                              <h3 className="font-bold uppercase" data-id="element-1444">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-1445">
                              <thead data-id="element-1446">
                                <tr className="bg-gray-100" data-id="element-1447">
                                  <th className="border border-black p-1.5 text-left" data-id="element-1448">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-1449">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1450">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-1451">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1452">
                                <tr className="align-top" data-id="element-1453">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-1454">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-1455">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-1456">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-1457">
                                        <span className="font-medium" data-id="element-1458">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-1459">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-1460">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-1461">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-1462">
                              <p className="font-bold uppercase mb-1" data-id="element-1463">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-1464">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-1465">{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-1466">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-1467">
                              <h3 className="font-bold uppercase" data-id="element-1468">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-1469">
                              <thead data-id="element-1470">
                                <tr className="bg-gray-100" data-id="element-1471">
                                  <th className="border border-black p-1.5 text-left" data-id="element-1472">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-1473">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-1474">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-1475">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-1476">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-1477">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-1478">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-1479">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-1480">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1481">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-1482">
                                              <span className="font-medium" data-id="element-1483">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-1484">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-1485">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-1486">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-1487">
                            <thead data-id="element-1488">
                              <tr className="bg-gray-100" data-id="element-1489">
                                <th className="border border-black p-2 text-left" data-id="element-1490">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-1491">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-1492">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-1493">
                                    <td className="border border-black p-2 font-medium" data-id="element-1494">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-1495">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-1496">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-1497">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-1498">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-1499">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-1500">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-1501">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-1502">
                            <span className="font-semibold" data-id="element-1503">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-1504">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-1505">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-1506">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-1507">
                              <h3 className="font-bold" data-id="element-1508">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-1509">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-1510">
                                  <ZoomOut className="w-4 h-4" data-id="element-1511" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-1512" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-1513">
                                  <ZoomIn className="w-4 h-4" data-id="element-1514" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-1515">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-1516">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-1517">
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-1518">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-1519" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-1520">
      <div className="flex justify-between items-center" data-id="element-1521">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1522">
          Clinician Dashboard
        </h1>
        <Badge variant="info" className="text-sm px-3 py-1" data-id="element-1523">
          {pendingEncounters.length} Patients Waiting
        </Badge>
      </div>

      <Card noPadding data-id="element-1524">
        <ul className="divide-y divide-gray-200" data-id="element-1525">
          {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1526">
                  <div className="flex items-center" data-id="element-1527">
                    <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-1528">
                      <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-1529" />
                    </div>
                    <div data-id="element-1530">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1531">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1532">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1" data-id="element-1533">
                        <Badge variant="default" data-id="element-1534">{patient.age} yrs</Badge>
                        {hasResults && <Badge variant="success" data-id="element-1535">Results Ready</Badge>}
                        {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-1536">Awaiting Lab</Badge>}
                        {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-1537">Awaiting Radiology</Badge>}
                        {isPharmacy && <Badge variant="purple" data-id="element-1538">At Pharmacy</Badge>}
                        {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-1539">Draft Saved</Badge>}
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-1540">
                    Open File
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1541">
              <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1542" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1543">
                No patients waiting
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};